import type { UserProfile } from '../services/firestore';
import {
  SHOPIFY_INSTALL_STEP_PATHS,
  type ShopifyInstallStepPath,
  type ShopifyInstallWizardState,
} from '../types/shopifyInstallWizard';
import {
  installWizardPath,
  readShopifyInstallWizardState,
} from './shopifyInstallWizardStorage';

export interface ResumeWizardOptions {
  profile: UserProfile | null;
  wizardState?: ShopifyInstallWizardState | null;
  /** User has 2+ workspaces and has not picked one for this shop */
  needsWorkspacePicker?: boolean;
  isAuthenticated?: boolean;
}

function isStepComplete(
  step: ShopifyInstallStepPath,
  completed: Set<ShopifyInstallStepPath>,
  profile: UserProfile | null,
  state: ShopifyInstallWizardState | null
): boolean {
  if (completed.has(step)) return true;

  switch (step) {
    case 'account':
      return !!(profile?.profileCompleted && profile?.workspaceCompleted);
    case 'workspace':
      return !state?.workspaceId ? false : completed.has('workspace') || !!state.workspaceId;
    case 'goals':
      return !!(profile?.signupGoal || state?.goal);
    case 'current-tool':
      return !!(profile?.previousEmailTool || state?.previousTool);
    case 'industry':
      return !!(
        state?.industry ||
        (profile?.industry && profile.industry !== 'Shopify Store')
      );
    case 'revenue':
      return !!(profile?.annualRevenueBand || state?.revenueBand);
    case 'contacts':
      return (
        typeof state?.subscriberCount === 'number' ||
        (typeof profile?.subscription?.subscribers === 'number' &&
          profile.subscription.subscribers > 0)
      );
    case 'attribution':
      return !!(profile?.referralChannel || profile?.howDidYouHear || state?.referralChannel);
    case 'billing':
      return profile?.planSelected === true;
    case 'sync':
      return profile?.shopifyInstallWizardCompleted === true || profile?.syncCompleted === true;
    default:
      return false;
  }
}

/** First install-wizard step the merchant still needs to complete. */
export function getResumeShopifyInstallWizardStep(options: ResumeWizardOptions): ShopifyInstallStepPath {
  const { profile, needsWorkspacePicker, isAuthenticated } = options;
  const state = options.wizardState ?? readShopifyInstallWizardState();
  const completed = new Set(state?.completedSteps || []);

  if (!isAuthenticated) {
    return 'account';
  }

  for (const step of SHOPIFY_INSTALL_STEP_PATHS) {
    if (step === 'workspace') {
      if (needsWorkspacePicker && !isStepComplete('workspace', completed, profile, state)) {
        return 'workspace';
      }
      continue;
    }
    if (!isStepComplete(step, completed, profile, state)) {
      return step;
    }
  }

  return 'sync';
}

export function getInstallWizardResumePath(options: ResumeWizardOptions): string {
  const state = options.wizardState ?? readShopifyInstallWizardState();
  const step = getResumeShopifyInstallWizardStep(options);
  return installWizardPath(step, state);
}
